import calculateImageSize from "../tools/calculateImageSize";
import errorCorrectionPercents from "../constants/errorCorrectionPercents";
import QRDot from "../figures/dot/svg/QRDot";
import QRCornerSquare from "../figures/cornerSquare/svg/QRCornerSquare";
import QRCornerDot from "../figures/cornerDot/svg/QRCornerDot";
import { RequiredOptions } from "./QROptions";
import gradientTypes from "../constants/gradientTypes";
import { QrMatrix, FilterFunction, Gradient } from "../types";

// Simple hash function to replace jsonStableHash
function simpleHash(obj: any): string {
  const str = JSON.stringify(obj);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return 'qr-' + Math.abs(hash).toString(36);
}

const squareMask = [
  [1, 1, 1, 1, 1, 1, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 0, 1],
  [1, 1, 1, 1, 1, 1, 1]
];

const dotMask = [
  [0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0],
  [0, 0, 1, 1, 1, 0, 0],
  [0, 0, 1, 1, 1, 0, 0],
  [0, 0, 1, 1, 1, 0, 0],
  [0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0]
];

const measureQr = (options: RequiredOptions, count: number) => {
  const { width, height, margin } = options;
  const minSize = Math.min(width, height) - margin * 2;
  // SVG coordinates are vector units, not raster pixels. Small symbols still
  // need all modules; flooring their module width to zero produces a blank QR.
  const fractional = options.roundModules === false || minSize < count;
  const dotSize = fractional ? minSize / count : Math.floor(minSize / count);
  const xBeginning = fractional ? margin : Math.floor((options.width - count * dotSize) / 2);
  const yBeginning = fractional ? margin : Math.floor((options.height - count * dotSize) / 2);
  return {
    count,
    minSize,
    dotSize,
    xBeginning,
    yBeginning
  };
};

const squarePath = (left: number, top: number, size: number): string =>
  `M ${left} ${top} h ${size} v ${size} h ${-size} Z`;

/**
 * Parse viewBox dimensions from an SVG string to get intrinsic width/height.
 * Returns { width, height } or null if parsing fails.
 */
function parseSvgViewBox(svgString: string): { width: number; height: number } | null {
  const match = svgString.match(/viewBox\s*=\s*"([^"]+)"/);
  if (!match) return null;
  const parts = match[1].split(/\s+/).map(Number);
  if (parts.length >= 4) {
    return { width: parts[2], height: parts[3] };
  }
  return null;
}

/**
 * Worker-safe QRSVG — produces SVG markup via string concatenation, no DOM required.
 */
export default class QRSVG {
  _options: RequiredOptions;
  _qr?: QrMatrix;
  _id?: string;

  // String buffers for SVG assembly
  _defsContent: string = "";
  _bodyContent: string = "";
  _vectorPaths: string[] = [];

  // Cached image dimensions (parsed from SVG data URL viewBox)
  _imageWidth: number = 0;
  _imageHeight: number = 0;

  constructor(options: RequiredOptions) {
    this._id = simpleHash(options);
    this._options = options;
  }

  get width(): number {
    return this._options.width;
  }

  get height(): number {
    return this._options.height;
  }

  /**
   * Returns the complete SVG markup string. No DOM required.
   */
  getSvgString(): string {
    const options = this._options;
    let viewBox;
    if (options.bezel) {
      viewBox = {
        x: -options.bezel.depth,
        y: -options.bezel.depth,
        width: options.width + options.bezel.depth,
        height: options.height + options.bezel.depth,
        ...options.viewBox
      };
    } else {
      viewBox = {
        x: 0,
        y: 0,
        width: options.width,
        height: options.height,
        ...options.viewBox
      };
    }

    const sizeAttrs = options.fit
      ? ""
      : ` width="${options.width}" height="${options.height}"`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}" shape-rendering="crispEdges"${sizeAttrs}>` +
      `<defs>${this._defsContent}</defs>` +
      this._bodyContent +
      `</svg>`;
  }

  /**
   * For backward compatibility with QRCodeStyling — returns a DOM SVGElement
   * by parsing the SVG string. Only use on the main thread.
   */
  getElement(): SVGElement {
    const parser = new DOMParser();
    const doc = parser.parseFromString(this.getSvgString(), "image/svg+xml");
    return doc.documentElement as unknown as SVGElement;
  }

  getVectorPath(): string {
    const options = this._options;
    if (options.dotsOptions.type !== "square" || options.cornersSquareOptions?.type !== "extra-rounded" ||
      options.cornersDotOptions?.type || options.image || options.bezel ||
      options.dotsOptions.gradient || options.cornersSquareOptions?.gradient ||
      options.cornersSquareOptions?.color || options.cornersSquareOptions?.colors ||
      options.cornersDotOptions?.gradient || options.cornersDotOptions?.color) {
      throw new Error("Vector paths require square modules and single-color extra-rounded finder corners.");
    }
    return this._vectorPaths.join(" ");
  }

  clear(): void {
    this._defsContent = "";
    this._bodyContent = "";
    this._vectorPaths = [];
  }

  async drawQR(qr: QrMatrix): Promise<void> {
    const count = qr.getModuleCount();
    const { dotSize } = measureQr(this._options, count);
    let drawImageSize = {
      hideXDots: 0,
      hideYDots: 0,
      width: 0,
      height: 0
    };

    this._qr = qr;

    if (this._options.image) {
      // Worker-safe image loading: parse viewBox from SVG data URL
      this._loadImageSync();
      if (this._imageWidth > 0 && this._imageHeight > 0) {
        const { imageOptions, qrOptions } = this._options;
        const coverLevel = imageOptions.imageSize * errorCorrectionPercents[qrOptions.errorCorrectionLevel];
        const maxHiddenDots = Math.floor(coverLevel * count * count);

        drawImageSize = calculateImageSize({
          originalWidth: this._imageWidth || imageOptions.width || 0,
          originalHeight: this._imageHeight || imageOptions.height || imageOptions.width || 0,
          maxHiddenDots,
          maxHiddenAxisDots: count - 14,
          dotSize
        });
      }
    }

    this.clear();
    this.drawBackground();
    this.drawDots((i: number, j: number): boolean => {
      if (this._options.imageOptions.hideBackgroundDots) {
        if (this._options.imageOptions.hideShape === "radial") {
          const r = (drawImageSize.hideXDots || this._options?.imageOptions?.hideManualSize || 0) * 0.5;
          const center = count / 2;
          const d = Math.pow(r, 2) - (Math.pow(center - i, 2) + Math.pow(center - j, 2));
          if (d > 0) return false;
        } else {
          if (
            i >= (count - drawImageSize.hideXDots) / 2 &&
            i < (count + drawImageSize.hideXDots) / 2 &&
            j >= (count - drawImageSize.hideYDots) / 2 &&
            j < (count + drawImageSize.hideYDots) / 2
          ) {
            return false;
          }
        }
      }

      if (squareMask[i]?.[j] || squareMask[i - count + 7]?.[j] || squareMask[i]?.[j - count + 7]) {
        return false;
      }

      if (dotMask[i]?.[j] || dotMask[i - count + 7]?.[j] || dotMask[i]?.[j - count + 7]) {
        return false;
      }

      return true;
    });
    this.drawCorners();

    if (this._options.image) {
      this.drawImage({ width: drawImageSize.width, height: drawImageSize.height, count, dotSize });
    }
  }

  /**
   * Synchronous image dimension extraction from SVG data URL.
   * Parses the viewBox to determine intrinsic width/height without DOM.
   */
  _loadImageSync(): void {
    const image = this._options.image;
    if (!image) return;

    if (image.indexOf("data:image/svg+xml;base64,") === 0) {
      const b64 = image.replace("data:image/svg+xml;base64,", "");
      const svgString = atob(b64);
      const dims = parseSvgViewBox(svgString);
      if (dims) {
        this._imageWidth = dims.width;
        this._imageHeight = dims.height;
      } else {
        // Fallback: try to parse width/height attributes
        const wMatch = svgString.match(/\bwidth\s*=\s*"(\d+(?:\.\d+)?)"/);
        const hMatch = svgString.match(/\bheight\s*=\s*"(\d+(?:\.\d+)?)"/);
        this._imageWidth = wMatch ? parseFloat(wMatch[1]) : 0;
        this._imageHeight = hMatch ? parseFloat(hMatch[1]) : 0;
      }
    } else {
      // For non-SVG images, use imageOptions fallbacks
      this._imageWidth = this._options.imageOptions.width || 0;
      this._imageHeight = this._options.imageOptions.height || this._options.imageOptions.width || 0;
    }
  }

  /**
   * For backward compatibility: async loadImage() that delegates to sync version.
   * The original used `new Image()` to measure dimensions; we parse the SVG instead.
   */
  loadImage(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this._options.image) {
        return reject("Image is not defined");
      }
      this._loadImageSync();
      if (this._imageWidth > 0 && this._imageHeight > 0) {
        resolve();
      } else {
        reject("Could not determine image dimensions");
      }
    });
  }

  drawBackground(): void {
    const options = this._options;

    const gradientOptions = options.backgroundOptions?.gradient;
    const color = options.backgroundOptions?.color;
    const marginColor = options.backgroundOptions?.marginColor;

    if (gradientOptions || color) {
      const count = this._qr?.getModuleCount() || 0;
      const { dotSize, xBeginning, yBeginning } = measureQr(options, count);
      const hasMargin = xBeginning > 0 || yBeginning > 0;
      const uniformBackground = !gradientOptions && marginColor === color;
      const insetBackground = hasMargin && !!marginColor && !uniformBackground;
      if (insetBackground) {
        this._bodyContent += `<rect x="0" y="0" height="${options.height}" width="${options.width}" fill="${marginColor}"/>`;
      }
      const x = insetBackground ? xBeginning : 0;
      const y = insetBackground ? yBeginning : 0;
      const width = insetBackground ? count * dotSize : options.width;
      const height = insetBackground ? count * dotSize : options.height;
      this._defsContent += `<clipPath id="${this._id}-clip-path-background-color"><rect x="${x}" y="${y}" height="${height}" width="${width}"/></clipPath>`;
      this._createColor({
        options: gradientOptions,
        color: color,
        additionalRotation: 0,
        x,
        y,
        height,
        width,
        name: "background-color"
      });
    }

    if (options.bezel) {
      const count = this._qr?.getModuleCount() || 0;
      const { dotSize, xBeginning, yBeginning } = measureQr(options, count);
      const color = options.bezel?.color || options.cornersSquareOptions?.color || "#000000";

      this._createBezel({
        options: options.bezel,
        dotSize,
        xBeginning,
        yBeginning,
        count,
        color: color,
        colors: options.cornersSquareOptions?.colors,
        additionalRotation: 0,
        x: 0,
        y: 0,
        height: options.height,
        width: options.width,
        name: "bezel"
      });
    }
  }

  drawDots(filter?: FilterFunction): void {
    if (!this._qr) {
      throw "QR code is not defined";
    }

    const options = this._options;
    const count = this._qr.getModuleCount();

    if (options.width <= options.margin * 2 || options.height <= options.margin * 2) {
      const minSize = options.margin * 2;
      throw new Error(`QR code canvas too small (${options.width}x${options.height}); minimum is ${minSize}x${minSize}.`);
    }

    const { dotSize, xBeginning, yBeginning } = measureQr(options, count);

    const dot = new QRDot({ type: options.dotsOptions.type });

    // Build clipPath content for dots
    let clipPathContent = "";

    this._createColor({
      options: options.dotsOptions?.gradient,
      color: options.dotsOptions.color,
      additionalRotation: 0,
      x: xBeginning,
      y: yBeginning,
      height: count * dotSize,
      width: count * dotSize,
      name: "dot-color"
    });

    for (let i = 0; i < count; i++) {
      for (let j = 0; j < count; j++) {
        if (filter && !filter(i, j)) {
          continue;
        }
        if (!this._qr?.isDark(i, j)) {
          continue;
        }

        dot.draw(
          xBeginning + i * dotSize,
          yBeginning + j * dotSize,
          dotSize * (options.dotsOptions?.scale ?? 1),
          (xOffset: number, yOffset: number): boolean => {
            if (i + xOffset < 0 || j + yOffset < 0 || i + xOffset >= count || j + yOffset >= count) return false;
            if (filter && !filter(i + xOffset, j + yOffset)) return false;
            return !!this._qr && this._qr.isDark(i + xOffset, j + yOffset);
          }
        );

        if (dot._svgString) {
          clipPathContent += dot._svgString;
          this._vectorPaths.push(squarePath(xBeginning + i * dotSize, yBeginning + j * dotSize,
            dotSize * (options.dotsOptions?.scale ?? 1)));
        }
      }
    }

    // Add the dots clipPath to defs
    this._defsContent += `<clipPath id="${this._id}-clip-path-dot-color">${clipPathContent}</clipPath>`;
  }

  drawCorners(): void {
    if (!this._qr) {
      throw "QR code is not defined";
    }

    const options = this._options;
    const count = this._qr.getModuleCount();
    const { dotSize, xBeginning, yBeginning } = measureQr(options, count);
    const cornersSquareSize = dotSize * 7;
    const cornersDotSize = dotSize * 3;

    [
      [0, 0, 0],
      [1, 0, Math.PI / 2],
      [0, 1, -Math.PI / 2]
    ].forEach(([column, row, rotation], i) => {
      const x = xBeginning + column * dotSize * (count - 7);
      const y = yBeginning + row * dotSize * (count - 7);
      let cornersSquareClipTarget = "dot-color"; // default: append to dots clipPath
      let cornersDotClipTarget = "dot-color";

      if (
        options.cornersSquareOptions?.gradient ||
        options.cornersSquareOptions?.colors ||
        options.cornersSquareOptions?.color
      ) {
        const clipId = `corners-square-color-${column}-${row}`;
        cornersSquareClipTarget = clipId;
        cornersDotClipTarget = clipId;

        this._createColor({
          options: options.cornersSquareOptions?.gradient,
          color: options.cornersSquareOptions?.colors
            ? options.cornersSquareOptions?.colors[i]
            : options.cornersSquareOptions?.color,
          additionalRotation: rotation,
          x,
          y,
          height: cornersSquareSize,
          width: cornersSquareSize,
          name: clipId
        });

        // We'll add the clipPath content below
        this._defsContent += `<clipPath id="${this._id}-clip-path-${clipId}">`;
      }

      let cornerSquareClipContent = "";
      if (options.cornersSquareOptions?.type) {
        const cornersSquare = new QRCornerSquare({ type: options.cornersSquareOptions.type });
        cornersSquare.draw(x, y, cornersSquareSize, rotation);

        if (cornersSquare._svgString) {
          cornerSquareClipContent += cornersSquare._svgString;
          if (cornersSquare._vectorPath) this._vectorPaths.push(cornersSquare._vectorPath);
        }
      } else {
        const dot = new QRDot({ type: options.dotsOptions.type });

        for (let i = 0; i < squareMask.length; i++) {
          for (let j = 0; j < squareMask[i].length; j++) {
            if (!squareMask[i]?.[j]) {
              continue;
            }

            dot.draw(
              x + i * dotSize,
              y + j * dotSize,
              dotSize,
              (xOffset: number, yOffset: number): boolean => !!squareMask[i + xOffset]?.[j + yOffset]
            );

            if (dot._svgString) {
              cornerSquareClipContent += dot._svgString;
            }
          }
        }
      }

      if (cornersSquareClipTarget !== "dot-color") {
        // Close the dedicated clipPath we opened above, with the content
        this._defsContent += `${cornerSquareClipContent}</clipPath>`;
      } else {
        // Append to the existing dots clipPath by injecting before it was closed
        // Since we already wrote the dots clipPath, we need to append to body instead
        // Actually, the original code appends to this._dotsClipPath. We need to add these
        // elements to the dots clip path. Let's use a post-processing approach:
        // We'll store extra clip content and merge into the dots clipPath at the end.
        this._appendToDotsClipPath(cornerSquareClipContent);
      }

      // Corner dots
      if (options.cornersDotOptions?.gradient || options.cornersDotOptions?.color) {
        const dotClipId = `corners-dot-color-${column}-${row}`;
        cornersDotClipTarget = dotClipId;

        this._createColor({
          options: options.cornersDotOptions?.gradient,
          color: options.cornersDotOptions?.color,
          additionalRotation: rotation,
          x: x + dotSize * 2,
          y: y + dotSize * 2,
          height: cornersDotSize,
          width: cornersDotSize,
          name: dotClipId
        });

        this._defsContent += `<clipPath id="${this._id}-clip-path-${dotClipId}">`;
      }

      let cornerDotClipContent = "";
      if (options.cornersDotOptions?.type) {
        const cornersDot = new QRCornerDot({ type: options.cornersDotOptions.type });
        cornersDot.draw(x + dotSize * 2, y + dotSize * 2, cornersDotSize, rotation);

        if (cornersDot._svgString) {
          cornerDotClipContent += cornersDot._svgString;
        }
      } else {
        const dot = new QRDot({ type: options.dotsOptions.type });

        for (let i = 0; i < dotMask.length; i++) {
          for (let j = 0; j < dotMask[i].length; j++) {
            if (!dotMask[i]?.[j]) {
              continue;
            }

            dot.draw(
              x + i * dotSize,
              y + j * dotSize,
              dotSize,
              (xOffset: number, yOffset: number): boolean => !!dotMask[i + xOffset]?.[j + yOffset]
            );

            if (dot._svgString) {
              cornerDotClipContent += dot._svgString;
              this._vectorPaths.push(squarePath(x + i * dotSize, y + j * dotSize, dotSize));
            }
          }
        }
      }

      if (cornersDotClipTarget !== "dot-color") {
        this._defsContent += `${cornerDotClipContent}</clipPath>`;
      } else {
        this._appendToDotsClipPath(cornerDotClipContent);
      }
    });
  }

  /**
   * Appends content to the dots clipPath that was already written to _defsContent.
   * We insert before the closing </clipPath> tag of the dots clipPath.
   */
  _appendToDotsClipPath(content: string): void {
    const clipPathId = `${this._id}-clip-path-dot-color`;
    const closingTag = "</clipPath>";
    const idx = this._defsContent.indexOf(`id="${clipPathId}"`);
    if (idx === -1) return;

    // Find the closing </clipPath> after this clipPath's opening
    const closeIdx = this._defsContent.indexOf(closingTag, idx);
    if (closeIdx === -1) return;

    this._defsContent =
      this._defsContent.slice(0, closeIdx) +
      content +
      this._defsContent.slice(closeIdx);
  }

  drawImage({
    width,
    height,
    count,
    dotSize
  }: {
    width: number;
    height: number;
    count: number;
    dotSize: number;
  }): void {
    const options = this._options;
    const xBeginning = Math.floor((options.width - count * dotSize) / 2);
    const yBeginning = Math.floor((options.height - count * dotSize) / 2);
    const dx = xBeginning + options.imageOptions.margin + (count * dotSize - width) / 2;
    const dy = yBeginning + options.imageOptions.margin + (count * dotSize - height) / 2;
    const dw = width - options.imageOptions.margin * 2;
    const dh = height - options.imageOptions.margin * 2;

    if (options?.image && typeof options?.image === "string") {
      const imageString = options.image as string;
      if (imageString?.indexOf("data:image/svg+xml;base64,") === 0) {
        const b64 = imageString.replace("data:image/svg+xml;base64,", "");
        const svgString = atob(b64);
        try {
          // Extract the inner content of the SVG.
          // Strategy: find the first <svg ...> tag, extract its content,
          // then look for a <g> wrapper inside.
          const innerContent = this._extractSvgInnerContent(svgString);

          // Get the viewBox dimensions for scaling
          const dims = parseSvgViewBox(svgString);
          if (dims && dims.width > 0 && dims.height > 0) {
            // Scale from SVG viewBox coordinates to target size
            const scaleX = dw / dims.width;
            const scaleY = dh / dims.height;
            this._bodyContent +=
              `<g transform="translate(${dx}, ${dy}) scale(${scaleX}, ${scaleY})">` +
              innerContent +
              `</g>`;
          } else {
            // No viewBox — just translate
            this._bodyContent +=
              `<g transform="translate(${dx}, ${dy})">` +
              innerContent +
              `</g>`;
          }
        } catch (error) {
          // Skip bad SVG image element
        }
      } else {
        // Non-SVG image: embed as <image>
        this._bodyContent +=
          `<image href="${options.image}" x="${dx}" y="${dy}" width="${dw}px" height="${dh}px"/>`;
      }
    }
  }

  /**
   * Extract the inner content of an SVG string (the content inside <svg>...</svg>).
   * If the SVG wraps everything in a single <g>, returns that <g>'s inner content.
   */
  _extractSvgInnerContent(svgString: string): string {
    // Remove the outer <svg ...> and </svg> tags
    let inner = svgString
      .replace(/<svg[^>]*>/, "")
      .replace(/<\/svg>\s*$/, "")
      .trim();

    // Check if the content is wrapped in a single <g>...</g>
    // If so, extract the <g> tag with its attributes and children (keep the <g> wrapper)
    const gMatch = inner.match(/^\s*<g\b[^>]*>([\s\S]*)<\/g>\s*$/);
    if (gMatch) {
      // Return the whole <g>...</g> — the caller wraps it in another <g> with transform
      return inner;
    }

    return inner;
  }

  _createColor({
    options,
    color,
    additionalRotation,
    x,
    y,
    height,
    width,
    name
  }: {
    options?: Gradient;
    color?: string;
    additionalRotation: number;
    x: number;
    y: number;
    height: number;
    width: number;
    name: string;
  }): void {
    const size = width > height ? width : height;
    let fillAttr: string;

    if (options) {
      let gradientMarkup: string;

      if (options.type === gradientTypes.radial) {
        let stopsMarkup = "";
        options.colorStops.forEach(({ offset, color }: { offset: number; color: string }) => {
          stopsMarkup += `<stop offset="${100 * offset}%" stop-color="${color}"/>`;
        });
        gradientMarkup =
          `<radialGradient id="${this._id}-${name}" gradientUnits="userSpaceOnUse"` +
          ` fx="${x + width / 2}" fy="${y + height / 2}"` +
          ` cx="${x + width / 2}" cy="${y + height / 2}" r="${size / 2}">` +
          stopsMarkup +
          `</radialGradient>`;
      } else {
        const rotation = ((options.rotation || 0) + additionalRotation) % (2 * Math.PI);
        const positiveRotation = (rotation + 2 * Math.PI) % (2 * Math.PI);
        let x0 = x + width / 2;
        let y0 = y + height / 2;
        let x1 = x + width / 2;
        let y1 = y + height / 2;

        if (
          (positiveRotation >= 0 && positiveRotation <= 0.25 * Math.PI) ||
          (positiveRotation > 1.75 * Math.PI && positiveRotation <= 2 * Math.PI)
        ) {
          x0 = x0 - width / 2;
          y0 = y0 - (height / 2) * Math.tan(rotation);
          x1 = x1 + width / 2;
          y1 = y1 + (height / 2) * Math.tan(rotation);
        } else if (positiveRotation > 0.25 * Math.PI && positiveRotation <= 0.75 * Math.PI) {
          y0 = y0 - height / 2;
          x0 = x0 - width / 2 / Math.tan(rotation);
          y1 = y1 + height / 2;
          x1 = x1 + width / 2 / Math.tan(rotation);
        } else if (positiveRotation > 0.75 * Math.PI && positiveRotation <= 1.25 * Math.PI) {
          x0 = x0 + width / 2;
          y0 = y0 + (height / 2) * Math.tan(rotation);
          x1 = x1 - width / 2;
          y1 = y1 - (height / 2) * Math.tan(rotation);
        } else if (positiveRotation > 1.25 * Math.PI && positiveRotation <= 1.75 * Math.PI) {
          y0 = y0 + height / 2;
          x0 = x0 + width / 2 / Math.tan(rotation);
          y1 = y1 - height / 2;
          x1 = x1 - width / 2 / Math.tan(rotation);
        }

        let stopsMarkup = "";
        options.colorStops.forEach(({ offset, color }: { offset: number; color: string }) => {
          stopsMarkup += `<stop offset="${100 * offset}%" stop-color="${color}"/>`;
        });
        gradientMarkup =
          `<linearGradient id="${this._id}-${name}" gradientUnits="userSpaceOnUse"` +
          ` x1="${Math.round(x0)}" y1="${Math.round(y0)}"` +
          ` x2="${Math.round(x1)}" y2="${Math.round(y1)}">` +
          stopsMarkup +
          `</linearGradient>`;
      }

      this._defsContent += gradientMarkup;
      fillAttr = `url(#${this._id}-${name})`;
    } else if (color) {
      fillAttr = color;
    } else {
      return;
    }

    this._bodyContent +=
      `<rect x="${x}" y="${y}" height="${height}" width="${width}"` +
      ` clip-path="url(#${this._id}-clip-path-${name})"` +
      ` fill="${fillAttr}"/>`;
  }

  _createBezel({
    options,
    color,
    colors,
    count,
    height,
    width,
    xBeginning,
    yBeginning,
    dotSize
  }: {
    options: {
      depth: number;
      strokeWidth?: number;
      strokeLineCap?: string;
    };
    xBeginning: number;
    yBeginning: number;
    dotSize: number;
    count: number;
    color: string;
    colors?: string[];
    additionalRotation: number;
    x: number;
    y: number;
    height: number;
    width: number;
    name: string;
  }): void {
    const sw = options?.strokeWidth || 3;
    const _depth = options.depth - sw / 3;
    const squares = [
      {
        center: [0, 0],
        lines: [
          [0, 0],
          [1, 0],
          [0, 1]
        ],
        color: colors ? colors[0] : color
      },
      {
        center: [1, 0],
        lines: [
          [0, 0],
          [1, 0]
        ],
        color: colors ? colors[1] : color
      },
      {
        center: [0, 1],
        lines: [
          [0, 0],
          [0, 1]
        ],
        color: colors ? colors[2] : color
      }
    ];
    for (const s of squares) {
      const middleL = dotSize * (count - 7);
      const ss = dotSize * 6.5;
      const cx = s.center[0] * middleL;
      const cy = s.center[1] * middleL;
      for (const l of s.lines) {
        const _x = xBeginning + cx + l[0] * ss;
        const _y = yBeginning + cy + l[1] * ss;
        this._bodyContent +=
          `<line x1="${_x}" y1="${_y}" x2="${_x - _depth}" y2="${_y - _depth}"` +
          ` stroke="${s.color}" stroke-width="${sw ? sw / 2 : 2}"` +
          ` stroke-linecap="${options?.strokeLineCap || "square"}"/>`;
      }
    }
    const edges = [
      [
        [0, 0],
        [1, 0]
      ],
      [
        [0, 0],
        [0, 1]
      ]
    ];
    for (const lineSegment of edges) {
      const [sv, ev] = lineSegment;
      const _depth = options.depth;
      this._bodyContent +=
        `<line x1="${sv[0] * width - _depth}" y1="${sv[1] * height - _depth}"` +
        ` x2="${ev[0] * width - _depth - (sw / 6) * ev[0]}" y2="${ev[1] * height - _depth - (sw / 6) * ev[1]}"` +
        ` stroke="${color}" stroke-width="${sw || 4}"/>`;
    }
  }
}
